import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Avatar, Text } from 'components';
import { Api } from 'lib';
import { REFERRAL_COLORS } from './theme';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 480px;
  height: 70vh;
  max-height: 600px;
  background-color: ${REFERRAL_COLORS.bg};
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const CloseButton = styled.button`
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: 16px;
  border: none;
  background-color: ${REFERRAL_COLORS.secondaryCircle};
  color: ${REFERRAL_COLORS.white};
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const CommentList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px 16px;
`;

const CommentRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-left: ${(props) => (props.$isReply ? '32px' : '0')};
  margin-bottom: 12px;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 4px;
`;

const ActionText = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${REFERRAL_COLORS.mutedText};
  font-size: 12px;
  font-weight: 600;
`;

const RepliesWrapper = styled.div`
  margin-bottom: 8px;
`;

const ReplyingToChip = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 6px 10px;
  margin: 0 16px 8px;
`;

const InputBar = styled.form`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const Input = styled.input`
  flex: 1;
  background-color: rgba(255, 255, 255, 0.08);
  border: none;
  border-radius: 999px;
  padding: 10px 14px;
  color: #fff;
  font-size: 14px;
  outline: none;
`;

const SendButton = styled.button`
  background-color: ${REFERRAL_COLORS.cta};
  border: none;
  border-radius: 999px;
  padding: 8px 16px;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

function CommentRowView({ comment, isReply, onReply, replyState, onToggleReplies }) {
  return (
    <>
      <CommentRow $isReply={isReply}>
        <Avatar user={comment.owner} small={!isReply} tiny={isReply} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text size="13px" bold col={REFERRAL_COLORS.white}>
            {comment.owner?.name || comment.owner?.username || 'Unknown'}
          </Text>
          <Text size="13px" col={REFERRAL_COLORS.white} marg="2px 0 0">
            {comment.body}
          </Text>
          <ActionRow>
            <ActionText type="button" onClick={() => onReply(comment)}>
              Reply
            </ActionText>
            {!isReply && (
              <ActionText type="button" onClick={() => onToggleReplies(comment._id)}>
                {replyState?.expanded
                  ? 'Hide replies'
                  : replyState?.items?.length
                    ? `View ${replyState.items.length} ${replyState.items.length === 1 ? 'reply' : 'replies'}`
                    : 'View replies'}
              </ActionText>
            )}
          </ActionRow>
        </div>
      </CommentRow>

      {!isReply && replyState?.expanded && (
        <RepliesWrapper>
          {replyState.items.length === 0 ? (
            <Text size="12px" col={REFERRAL_COLORS.mutedText} style={{ marginLeft: 32 }}>
              No replies yet.
            </Text>
          ) : (
            replyState.items.map((r) => (
              <CommentRowView key={r._id} comment={r} isReply onReply={onReply} />
            ))
          )}
        </RepliesWrapper>
      )}
    </>
  );
}

// Standalone comments bottom sheet matching FanCamCommentsBottomSheet's
// visual shape and reply behavior on mobile (components/Comments/CommentItem)
// — deliberately NOT reusing playaz-cms's existing components/Post comment UI
// (reported broken), just the generic /comments API (same one Post itself
// calls under the hood). Every top-level comment can be replied to, and
// replies thread underneath via the same replyTo query param the mobile app
// uses, fetched lazily on "View replies".
const ReferralCommentsSheet = ({ fancamId, onClose }) => {
  const currentUser = useSelector((state) => state.user);
  const [comments, setComments] = useState([]);
  const [replies, setReplies] = useState({}); // { [parentId]: { expanded, items } }
  const [replyingTo, setReplyingTo] = useState(null); // { id, username }
  const [body, setBody] = useState('');
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (!fancamId) return;
    Api.get(`/comments/fancam/${fancamId}`).then((res) => {
      if (res?.comments) setComments(res.comments);
    });
  }, [fancamId]);

  async function handleToggleReplies(parentId) {
    const existing = replies[parentId];
    if (existing?.expanded) {
      setReplies((prev) => ({ ...prev, [parentId]: { ...existing, expanded: false } }));
      return;
    }
    if (existing?.items) {
      setReplies((prev) => ({ ...prev, [parentId]: { ...existing, expanded: true } }));
      return;
    }
    const res = await Api.get(`/comments/fancam/${fancamId}?replyTo=${parentId}`);
    setReplies((prev) => ({
      ...prev,
      [parentId]: { expanded: true, items: res?.comments || [] }
    }));
  }

  function handleReply(comment) {
    // Threads under the same top-level parent, same as mobile's CommentItem.handleReply.
    const parentId = comment.replyTo ?? comment._id;
    setReplyingTo({ id: parentId, username: comment.owner?.username || 'user' });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const trimmed = body.trim();
    if (!trimmed || isSending) return;
    setIsSending(true);
    const res = await Api.post('/comments/create', {
      entityType: 'fancam',
      entityId: fancamId,
      body: trimmed,
      taggedUsers: [],
      ...(replyingTo ? { replyTo: replyingTo.id } : {})
    });
    setIsSending(false);
    if (res) {
      if (replyingTo) {
        setReplies((prev) => {
          const existing = prev[replyingTo.id] || { expanded: true, items: [] };
          return {
            ...prev,
            [replyingTo.id]: {
              expanded: true,
              items: [...existing.items, { ...res, owner: currentUser }]
            }
          };
        });
      } else {
        setComments((prev) => [{ ...res, owner: currentUser }, ...prev]);
      }
      setBody('');
      setReplyingTo(null);
    }
  }

  return (
    <Backdrop onClick={onClose}>
      <Sheet onClick={(e) => e.stopPropagation()}>
        <Header>
          <Text b1 bold col={REFERRAL_COLORS.white}>
            Comments
          </Text>
          <CloseButton onClick={onClose} aria-label="Close">
            ✕
          </CloseButton>
        </Header>
        <CommentList>
          {comments.length === 0 && (
            <Text b2 col={REFERRAL_COLORS.mutedText}>
              No comments yet. Be the first!
            </Text>
          )}
          {comments.map((c) => (
            <CommentRowView
              key={c._id}
              comment={c}
              onReply={handleReply}
              replyState={replies[c._id]}
              onToggleReplies={handleToggleReplies}
            />
          ))}
        </CommentList>

        {replyingTo && (
          <ReplyingToChip>
            <Text size="12px" col={REFERRAL_COLORS.mutedText}>
              Replying to @{replyingTo.username}
            </Text>
            <ActionText type="button" onClick={() => setReplyingTo(null)}>
              ✕
            </ActionText>
          </ReplyingToChip>
        )}

        <InputBar onSubmit={handleSubmit}>
          <Input
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={replyingTo ? `Reply to @${replyingTo.username}...` : 'Add a comment...'}
          />
          <SendButton type="submit" disabled={!body.trim() || isSending}>
            Post
          </SendButton>
        </InputBar>
      </Sheet>
    </Backdrop>
  );
};

export default ReferralCommentsSheet;
